import axios from "axios";
import { store } from "../reducers/store";
import { addAlert } from "../reducers/alertSlice";
import { cryptoStorage } from "./crypto";
import { createSessionGate, requestSessionRefresh } from "./sessionAccess";

// ============ DEDUPLICARE REQUESTURI ============
// Previne apeluri duplicate în caz de bucle infinite în componente
const requestCounts = new Map();
const DUPLICATE_THRESHOLD = 10; // Max 10 apeluri identice pe secundă
const RESET_INTERVAL = 1000; // Reset contor la fiecare secundă

const getRequestKey = (config) => {
  const { method, url, params, data } = config;
  return `${method}:${url}:${JSON.stringify(params || {})}:${JSON.stringify(data || {})}`;
};

const deduplicateRequest = (config) => {
  const key = getRequestKey(config);
  const now = Date.now();

  // Verifică dacă avem prea multe apeluri identice
  const countData = requestCounts.get(key);
  if (countData) {
    if (now - countData.timestamp < RESET_INTERVAL) {
      countData.count++;
      if (countData.count > DUPLICATE_THRESHOLD) {
        console.warn(`[API Guard] Blocat! Prea multe apeluri (${countData.count}) către: ${config.url}`);
        const error = new Error(`[API Guard] Request blocat: prea multe apeluri către ${config.url}`);
        error.isApiGuardBlocked = true;
        throw error;
      }
    } else {
      // Reset după interval
      countData.count = 1;
      countData.timestamp = now;
    }
  } else {
    requestCounts.set(key, { count: 1, timestamp: now });
  }

  return config;
};

// Curăță periodic map-urile pentru a evita memory leaks
setInterval(() => {
  const now = Date.now();
  requestCounts.forEach((value, key) => {
    if (now - value.timestamp > 60000) { // Șterge după 1 minut de inactivitate
      requestCounts.delete(key);
    }
  });
}, 30000);

// ============ END DEDUPLICARE ============

export const backendClient = axios.create({
  baseURL: `${process.env.REACT_APP_SERVER_URL}/backend`,
  headers: {},
});

backendClient.interceptors.request.use(async (config) => {
  // Verifică apeluri duplicate
  config = deduplicateRequest(config);
  await ensureSession();
  config.sessionUserId = store.getState().userReducer?.user?.id;

  const token = cryptoStorage.getItem('token');
  if (!!token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  const key = cryptoStorage.getItem('backend-key');
  if (!!key) {
    config.headers['Backend-key'] = key;
  }

  return config;
});

export const commonClient = axios.create({
  baseURL: `${process.env.REACT_APP_SERVER_URL}/common`,
  headers: {},
});

const ensureSession = createSessionGate({
  getUser: () => store.getState().userReducer?.user,
  renew: (refresh) => requestSessionRefresh(commonClient, refresh),
  accept: (response) => store.dispatch({ type: "user/refreshSession", payload: response }),
  expire: () => { import("../reducers/userSlice").then(({ signOutAsync }) => store.dispatch(signOutAsync())); },
});

// export const reportClient = axios.create({
//   baseURL: `${process.env.REACT_APP_SERVER_URL}/reports`,
//   headers: {},
// });

export const reportClient =
  process.env.NODE_ENV === "development"
    ? axios.create({
        baseURL: "https://efacturaapi020323.azure-api.net/reports",
        headers: {
          //   Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJmaXJzdE5hbWUiOiJ0ZXN0IiwibGFzdE5hbWUiOiJ1c2VyIiwiZW1haWwiOiJ0ZXN0QG1haWwuY29tIiwidGVsZXBob25lIjoiMDc0MTMyOTcxMCIsInVzZXJuYW1lIjoidGVzdEBtYWlsLmNvbSIsImhhc0F1dGhlbnRpY2F0b3IiOmZhbHNlLCJpZCI6ImQ2NTkzYWRjLWU1NDItNTkxNy1iNDYzLTk4MjYzMGMyOGY0MCIsInN1YnNjcmlwdGlvbktleXMiOnsiYmFja2VuZCI6ImFiYyIsImVmYWN0dXJhIjoiZGVmIiwicmVwb3J0IjoiZ2hpIn0sInNjb3BlIjpbImJhY2tlbmQiLCJlZmFjdHVyYSIsInJlcG9ydCJdLCJpYXQiOjE2OTExMjk3MjMsImV4cCI6MTY5MzcyMTcyM30.i97P5LHh_fsCRi6A74QJUAabQoLWypnBonQw7PfgwBg`,
        },
      })
    : axios.create({
        baseURL: `${process.env.REACT_APP_SERVER_URL}/reports`,
        headers: {
          //   Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJmaXJzdE5hbWUiOiJ0ZXN0IiwibGFzdE5hbWUiOiJ1c2VyIiwiZW1haWwiOiJ0ZXN0QG1haWwuY29tIiwidGVsZXBob25lIjoiMDc0MTMyOTcxMCIsInVzZXJuYW1lIjoidGVzdEBtYWlsLmNvbSIsImhhc0F1dGhlbnRpY2F0b3IiOmZhbHNlLCJpZCI6ImQ2NTkzYWRjLWU1NDItNTkxNy1iNDYzLTk4MjYzMGMyOGY0MCIsInN1YnNjcmlwdGlvbktleXMiOnsiYmFja2VuZCI6ImFiYyIsImVmYWN0dXJhIjoiZGVmIiwicmVwb3J0IjoiZ2hpIn0sInNjb3BlIjpbImJhY2tlbmQiLCJlZmFjdHVyYSIsInJlcG9ydCJdLCJpYXQiOjE2OTExMjk3MjMsImV4cCI6MTY5MzcyMTcyM30.i97P5LHh_fsCRi6A74QJUAabQoLWypnBonQw7PfgwBg`,
        },
      });

reportClient.interceptors.request.use(async (config) => {
  // Verifică apeluri duplicate
  config = deduplicateRequest(config);
  await ensureSession();
  config.sessionUserId = store.getState().userReducer?.user?.id;

  const token = cryptoStorage.getItem('token');
  if (!!token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  const key = cryptoStorage.getItem('report-key');
  if (!!key) {
    config.headers['Report-key'] = key;
  }

  return config;
});

// Adaugă deduplicare și pentru commonClient
commonClient.interceptors.request.use((config) => {
  return deduplicateRequest(config);
});

// Retry only an explicit authentication rejection, once. Company/ANAF 403s
// and network failures must not replay mutations or discard the user's session.
[backendClient, reportClient].forEach((client) => client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const user = store.getState().userReducer?.user;
    if (error.response?.status !== 401 || !config || config.url === "/RefreshSession" || config.sessionRetried || !user?.refreshToken || config.sessionUserId !== user.id) throw error;
    const token = config.headers?.Authorization?.replace(/^Bearer /, "");
    await ensureSession({ force: true, token });
    return client.request({ ...config, sessionRetried: true });
  },
));

let isServerErrorNotified = false;

const notifyServerError = (
  msg,
  { title = "Eroare de Server", type = "error", id = "server-error" } = {},
) => {
  if (!isServerErrorNotified) {
    store.dispatch(
      addAlert({
        type,
        title,
        id,
        message: msg,
        delay: 3,
      })
    );
    isServerErrorNotified = true;

    setTimeout(() => {
      isServerErrorNotified = false;
    }, 2000);
  }
};

export const isCompanyAuthorizationError = (error) => {
  const responseData = error?.response?.data;
  const errorMessage = [
    responseData?.message,
    responseData?.cause,
    responseData?.error?.message,
  ].find((message) => typeof message === "string");

  return errorMessage
    ?.toLowerCase()
    .includes("not authorized for company");
};

const addInterceptorToClient = (client) => {
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isCompanyAuthorizationError(error)) {
        notifyServerError(
          "Firma selectata nu este autorizata pentru comunicarea cu ANAF. Autorizeaz-o din Conectare SPV.",
          {
            title: "Firma neautorizata",
            type: "warning",
            id: "company-not-authorized",
          },
        );
      } else if (error.response && error.response.status === 500) {
        notifyServerError(
          "Serverul este momentan indisponibil. Vă rugăm să încercați din nou mai târziu."
        );
      }
      if (error.message && error.message === "Network Error")
        notifyServerError(
          "Serverul este momentan indisponibil. Verificați conexiunea la internet."
        );

      return Promise.reject(error);
    }
  );
};

export const setupAxiosInterceptors = () => {
  isServerErrorNotified = false;

  [backendClient, commonClient, reportClient].forEach(addInterceptorToClient);
};
